/**
 * README
 * This is an "internal" component meant to be used solely by other RC components.
 * Please don't use this directly. If it seems like you'd benefit from it please
 * reach out to #cadence for help coordinating!
 */

import React, { useLayoutEffect, useState } from 'react';
import classNames from 'classnames';

import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  UseDismissProps,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react';

import { RCCloseButton, RCCloseButtonProps } from '../../RCCloseButton/RCCloseButton';
import { FloatingPortalRootContext } from '../../hooks/useFloatingPortalTarget';
import { useRegisteredId } from '../../hooks/useRegisteredId';
import { MODAL_DOM_ID } from '../../../constants';
import { PartsContext, usePartsContext } from './context';
import { Actions } from './RCModalBaseActions';

type ElementRef = React.MutableRefObject<HTMLElement | null>;

/**
 * Lets content rendered inside the modal close the modal containing it.
 *
 * Internal — consumers reach this through the hook their wrapper exports (`useRCModal`,
 * `useRCDrawer`).
 *
 * Defaults to a no-op rather than `null` so a component reused both inside and outside a modal
 * doesn't crash on the outside path.
 */
export interface ModalContextValue {
  close: () => void;
}

const RCModalBaseContext = React.createContext<ModalContextValue>({ close: () => {} });

export const useRCModalBaseContext = () => React.useContext(RCModalBaseContext);

export interface RCModalBaseRootProps {
  isOpen?: boolean;
  onOpenChange?: (state: boolean) => void;
  initialFocus?: 0 | ElementRef;
  role?: 'dialog' | 'alertdialog';
  /** Renders an overlay and traps focus. Turn off for a panel that leaves the page usable. */
  isModal?: boolean;
  useDismissProps?: UseDismissProps;
  children: React.ReactNode;
}

function useModalBase({
  isOpen: openProp,
  onOpenChange: setOpenProp,
  initialFocus,
  role,
  isModal,
  useDismissProps,
}: Omit<RCModalBaseRootProps, 'children'>) {
  const modalId = useId();
  const [openState, setOpenState] = useState<boolean>(openProp || false);
  const [focusRef, setFocusRef] = useState<ElementRef>(initialFocus === 0 ? null : initialFocus);
  const [shouldReturnFocus, setShouldReturnFocus] = useState<boolean>(true);
  const [hasTrigger, setHasTrigger] = useState(false);
  const [headingId, setHeadingId] = useState<string>();
  const [descriptionId, setDescriptionId] = useState<string>();

  const isControlled = openProp !== undefined && setOpenProp !== undefined;
  const open = isControlled ? openProp : openState;
  const setOpen = isControlled ? setOpenProp : setOpenState;

  const handleOpenChange = React.useCallback((state: boolean) => {
    setOpen(state);
    if (!isControlled) {
      setOpenProp?.(state);
    }
  }, [setOpen, isControlled, setOpenProp]);

  const focus = React.useMemo(() => {
    if (focusRef !== null) return focusRef;
    if (initialFocus) return initialFocus;
    return 0;
  }, [initialFocus, focusRef]);

  // Necessary to prevent Safari from focusing the <body> element on modal close when modal is externally controlled.
  // In other browsers, the trigger element receives focus on initial click and will receive focus again on modal close.
  // In all browsers, focus should return to the passed trigger if present.
  React.useEffect(() => {
    if (open) {
      const validActiveElement = document.activeElement !== document.body && document.activeElement !== null;
      setShouldReturnFocus(hasTrigger || validActiveElement);
    }
  }, [open, hasTrigger]);

  const { refs, context } = useFloating({
    open,
    onOpenChange: handleOpenChange,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useRole(context, {
      role,
    }),
    useDismiss(context, useDismissProps),
    useClick(context, {
      enabled: true,
    }),
  ]);

  const { isMounted, status } = useTransitionStatus(context);

  return {
    modalId,
    refs,
    context,
    isModal,
    isMounted,
    status,
    focus,
    shouldReturnFocus,
    getReferenceProps,
    getFloatingProps,
    handleOpenChange,
    setFocusRef,
    setHasTrigger,
    headingId,
    setHeadingId,
    descriptionId,
    setDescriptionId,
  };
}

export type ModalBaseValue = ReturnType<typeof useModalBase>;

/** Owns the open state, focus and interactions that the other parts share. */
function Root({
  isOpen = undefined,
  onOpenChange = undefined,
  initialFocus = 0,
  role = 'dialog',
  isModal = true,
  useDismissProps = {},
  children,
}: RCModalBaseRootProps) {
  const modal = useModalBase({
    isOpen,
    onOpenChange,
    initialFocus,
    role,
    isModal,
    useDismissProps,
  });
  const { handleOpenChange } = modal;

  // Built on `handleOpenChange`, not the raw `setOpen`: closing from inside the modal has to run
  // the same path as Escape or an overlay click, or an uncontrolled modal would close without ever
  // firing the consumer's `onOpenChange`.
  const closeValue = React.useMemo(
    () => ({ close: () => handleOpenChange(false) }),
    [handleOpenChange],
  );

  return (
    <PartsContext.Provider value={modal}>
      <RCModalBaseContext.Provider value={closeValue}>
        {children}
      </RCModalBaseContext.Provider>
    </PartsContext.Provider>
  );
}

/** The element that opens the modal. Clones its only child, adding the ref and interaction props. */
function Trigger({ children }: { children: JSX.Element }) {
  const { refs, getReferenceProps, setHasTrigger } = usePartsContext('Trigger');
  const ref = useMergeRefs([refs.setReference, (children as any).ref]);

  useLayoutEffect(() => {
    setHasTrigger(true);
    return () => setHasTrigger(false);
  }, [setHasTrigger]);

  return React.cloneElement(children, {
    ...getReferenceProps({ ref }),
  });
}

interface ContentProps {
  className?: string;
  position?: 'center' | 'bottom' | 'left' | 'right';
  displayStyle?: 'panel' | 'drawer';
  /** Keeps the content in the DOM while closed. */
  keepMounted?: boolean;
  /** Names the dialog by the id of an element the consumer renders. Only for when no `Heading` renders. */
  'aria-labelledby'?: string;
  children: React.ReactNode;
}

/**
 * The dialog itself, with its overlay and focus trap. It's named by a `Heading` inside it and
 * described only when a `Description` renders.
 */
function Content({
  className = undefined,
  position = 'center',
  displayStyle = 'panel',
  keepMounted = false,
  'aria-labelledby': ariaLabelledby = undefined,
  children,
}: ContentProps) {
  const {
    refs,
    context,
    isModal,
    isMounted,
    status,
    focus,
    shouldReturnFocus,
    getFloatingProps,
    headingId,
    descriptionId,
  } = usePartsContext('Content');

  // Held in state, not a ref: `FloatingPortal` waits for a `null` root and portals once it's set.
  const [floatingRoot, setFloatingRoot] = useState<HTMLDivElement | null>(null);

  const showOverlay = keepMounted ? isModal && status !== 'unmounted' : isModal && isMounted;

  if (!isMounted && !keepMounted) return null;

  return (
    <FloatingPortal id={MODAL_DOM_ID}>
      <div
        className="rc-modal-base"
        data-status={status}
      >
        {showOverlay &&
          <FloatingOverlay
            className="rc-modal-base__overlay"
            lockScroll
          />
        }
        <FloatingFocusManager
          context={context}
          order={['content']}
          initialFocus={focus}
          returnFocus={shouldReturnFocus}
          modal={isModal}
          closeOnFocusOut={false}
          disabled={!isMounted}
          getInsideElements={() => (floatingRoot ? [floatingRoot] : [])}
        >
          <div className="rc-modal-base__wrapper">
            <div
              className={classNames(
                'rc-modal-base__content',
                { 'rc-modal-base__content--bottom': position === 'bottom' },
                { 'rc-modal-base__content--left': position === 'left' },
                { 'rc-modal-base__content--right': position === 'right' },
                { 'rc-modal-base__content--drawer': displayStyle === 'drawer' },
              )}
            >
              <div
                className={className}
                {...getFloatingProps({
                  ref: refs.setFloating,
                  'aria-labelledby': ariaLabelledby ?? headingId,
                  'aria-describedby': descriptionId,
                })}
              >
                <FloatingPortalRootContext.Provider value={floatingRoot}>
                  {children}
                </FloatingPortalRootContext.Provider>
              </div>
            </div>
          </div>
        </FloatingFocusManager>
        {/* Floating surfaces opened inside the modal render here: after the wrapper so they paint
            above it, and outside the content so its transform doesn't clip them. */}
        <div
          className="rc-modal-base__floating-root"
          ref={setFloatingRoot}
        />
      </div>
    </FloatingPortal>
  );
}

/** Names the modal, as its `h2`. Children must be phrasing content (text, or a `span`). */
function Heading({ className = undefined, children }: { className?: string, children: React.ReactNode }) {
  const { setHeadingId } = usePartsContext('Heading');
  const id = useId();
  useRegisteredId(id, setHeadingId);

  return <h2 id={id} className={className}>{children}</h2>;
}

/** Describes the modal. Leave it out for structured or interactive content, which a screen reader
 * would otherwise read in full when the modal opens. */
function Description({ className = undefined, children }: { className?: string, children: React.ReactNode }) {
  const { setDescriptionId } = usePartsContext('Description');
  const id = useId();
  useRegisteredId(id, setDescriptionId);

  return <div id={id} className={className}>{children}</div>;
}

interface CloseButtonProps {
  variant?: RCCloseButtonProps['variant'];
}

const CloseButton = React.forwardRef<HTMLButtonElement, CloseButtonProps>(
  function CloseButton({
    variant = 'transparent',
  }: CloseButtonProps, ref) {
    const { handleOpenChange } = usePartsContext('CloseButton');
    return (
      <div className="rc-modal-base__close-button">
        <RCCloseButton
          onClick={() => handleOpenChange(false)}
          ref={ref}
          variant={variant}
        />
      </div>
    );
  },
);

/** _Note: This is an **internal** component. Please reach out to #cadence before using._ */
export const RCModalBase = {
  Root,
  Trigger,
  Content,
  Heading,
  Description,
  CloseButton,
  Actions,
};
